import { useState, useEffect } from "react";
import * as THREE from "three";

/**
 * Hook for targeting meshes by clicking on them in the 3D scene
 * Handles both individual meshes and grouped meshes
 */
const useTargetMesh = ({
  containerRef,
  cameraRef,
  modelRef,
  viewportRef = null,
  modelGroups = [],
}) => {
  const [isTargetMeshMode, setIsTargetMeshMode] = useState(false);
  const [targetedIndices, setTargetedIndices] = useState([]);

  // Build a lookup map: meshIndex -> groupIndex
  const buildGroupLookup = () => {
    const lookup = new Map();
    modelGroups.forEach((group, groupIdx) => {
      group.indices.forEach((meshIdx) => {
        lookup.set(meshIdx, groupIdx);
      });
    });
    return lookup;
  };

  // Raycasting effect
  useEffect(() => {
    if (!isTargetMeshMode) return;

    const handleMeshClick = (event) => {
      if (!containerRef.current || !cameraRef.current || !modelRef.current)
        return;

      // Check if the click is on a UI element
      if (event.target.closest(".ui-outside-scene")) return;

      const rect = containerRef.current.getBoundingClientRect();
      
      // Use viewport dimensions if available, otherwise use container dimensions
      const viewport = viewportRef?.current || {
        left: 0,
        top: 0,
        width: rect.width,
        height: rect.height,
      };
      
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      
      const x = ((mouseX - viewport.left) / viewport.width) * 2 - 1;
      const y = -((mouseY - viewport.top) / viewport.height) * 2 + 1;

      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(new THREE.Vector2(x, y), cameraRef.current);

      // Only check visible meshes
      const visibleMeshes = modelRef.current.children.filter(
        (child) => child.visible && child.isMesh
      );
      const intersects = raycaster.intersectObjects(visibleMeshes, false);

      if (intersects.length > 0) {
        const clickedMesh = intersects[0].object;
        const meshIndex = modelRef.current.children.indexOf(clickedMesh);

        if (meshIndex !== -1) {
          // Check if this mesh is part of a group
          const groupLookup = buildGroupLookup();
          const groupIndex = groupLookup.get(meshIndex);

          let targetKey;
          if (groupIndex !== undefined) {
            // Mesh is part of a group - use group key
            targetKey = `group-${groupIndex}`;
          } else {
            // Mesh is individual - use mesh key
            targetKey = `mesh-${meshIndex}`;
          }

          // Add to top of targeted list (remove if already exists to move it to top)
          setTargetedIndices((prev) => {
            const filtered = prev.filter((key) => key !== targetKey);
            return [targetKey, ...filtered];
          });
        }
      }
    };

    containerRef.current?.addEventListener("click", handleMeshClick);

    return () => {
      containerRef.current?.removeEventListener("click", handleMeshClick);
    };
  }, [isTargetMeshMode, modelGroups, containerRef, cameraRef, modelRef]);

  const toggleTargetMeshMode = () => {
    setIsTargetMeshMode((prev) => {
      if (prev) {
        // Disabling mode - clear targeted meshes
        setTargetedIndices([]);
      }
      return !prev;
    });
  };

  return {
    isTargetMeshMode,
    toggleTargetMeshMode,
    targetedIndices,
  };
};

export default useTargetMesh;
